// Lector SharePoint del Dashboard JMA AFAC.
//
// Slug y ruta confirmados desde el navegador (vista AllItems.aspx):
//   https://jmaseguridad.sharepoint.com/sites/JMACyberSec
//      └─ Documentos compartidos
//            └─ AFAC
//                  └─ Documentación para Consultoria   ← raíz del proyecto
//
// La librería de documentos por defecto del sitio queda accesible como
// /sites/{siteId}/drive. La ruta "AFAC/Documentación para Consultoria"
// se construye a partir de la raíz del drive, no desde un subsite.

import { getGraphClient } from './graph-client.js'

export const TENANT_HOST = 'jmaseguridad.sharepoint.com'
export const SITE_SLUG = 'JMACyberSec'
export const PROJECT_ROOT = 'AFAC/Documentación para Consultoria'

const ALLOWED_EXTENSIONS = ['.docx', '.xlsx', '.pptx', '.pdf']
const PAGE_SIZE = 200

let cachedSiteId = null

function isAllowedFile(name) {
  const lower = String(name || '').toLowerCase()
  return ALLOWED_EXTENSIONS.some((ext) => lower.endsWith(ext))
}

/**
 * Codifica cada segmento de un path independientemente y vuelve a unir
 * con `/`. encodeURIComponent escapa las tildes y espacios pero deja
 * la barra intacta como separador.
 *
 * Ejemplo:
 *   "AFAC/Documentación para Consultoria"
 *     → "AFAC/Documentaci%C3%B3n%20para%20Consultoria"
 */
function encodePathSegments(path) {
  return String(path || '')
    .split('/')
    .map((seg) => encodeURIComponent(seg))
    .join('/')
}

/**
 * Resuelve el siteId del sitio JMACyberSec.
 *   1. Primario: /sites/{tenant}:/sites/{SITE_SLUG}
 *   2. Fallback: /me/followedSites (buscando webUrl que termine en /sites/JMACyberSec).
 */
export async function getSiteId() {
  if (cachedSiteId) return cachedSiteId
  const client = getGraphClient()

  // Intento 1 — endpoint directo con slug ASCII confirmado.
  try {
    const site = await client
      .api(`/sites/${TENANT_HOST}:/sites/${SITE_SLUG}`)
      .select('id,name,displayName,webUrl')
      .get()
    if (site?.id) {
      cachedSiteId = site.id
      // eslint-disable-next-line no-console
      console.info('[sharepoint-reader] siteId resuelto vía path:', site.id, '·', site.webUrl)
      return site.id
    }
  } catch (err) {
    if (err?.statusCode && err.statusCode !== 404) throw err
    // eslint-disable-next-line no-console
    console.warn('[sharepoint-reader] Path directo no devolvió siteId, probando fallback.')
  }

  // Intento 2 — /me/followedSites.
  try {
    const followed = await client
      .api('/me/followedSites')
      .select('id,name,displayName,webUrl')
      .get()
    const candidates = followed?.value || []
    const expectedSuffix = `/sites/${SITE_SLUG.toLowerCase()}`
    const match =
      candidates.find(
        (s) => (s.webUrl || '').toLowerCase().endsWith(expectedSuffix)
      ) ||
      candidates.find((s) => s.name === SITE_SLUG) ||
      candidates.find(
        (s) => (s.name || '').toLowerCase() === SITE_SLUG.toLowerCase()
      )
    if (match?.id) {
      cachedSiteId = match.id
      // eslint-disable-next-line no-console
      console.info('[sharepoint-reader] siteId resuelto vía followedSites:', match.id, '·', match.webUrl)
      return match.id
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[sharepoint-reader] /me/followedSites también falló:', err?.message || err)
  }

  throw new Error(
    `[sharepoint-reader] No se pudo resolver el siteId de "${SITE_SLUG}" en ${TENANT_HOST}.`
  )
}

export function resetSiteCache() {
  cachedSiteId = null
}

/**
 * Llama al endpoint /sites/{siteId}/drive/root:/{path}:/children y maneja
 * `@odata.nextLink` agregando todas las páginas.
 */
async function listChildren(siteId, folderPath) {
  const client = getGraphClient()
  const encoded = encodePathSegments(folderPath)
  let endpoint = `/sites/${siteId}/drive/root:/${encoded}:/children`
  let useTop = true
  const all = []
  while (endpoint) {
    let request = client.api(endpoint)
    if (useTop) {
      request = request.top(PAGE_SIZE)
      useTop = false
    }
    const response = await request.get()
    if (Array.isArray(response.value)) all.push(...response.value)
    endpoint = response['@odata.nextLink'] || null
  }
  return all
}

/**
 * Recorre recursivamente la carpeta raíz del proyecto (BFS).
 *
 * @param {object} [opts]
 * @param {(scanned:number, queueSize:number) => void} [opts.onProgress]
 *   Callback invocado al terminar de listar cada carpeta.
 * @returns {Promise<{
 *   siteId: string,
 *   files: Array<object>,
 *   folders: Array<object>,
 *   scannedAt: string
 * }>}
 */
export async function listProjectInventory({ onProgress } = {}) {
  const siteId = await getSiteId()
  // eslint-disable-next-line no-console
  console.info(`[sharepoint-reader] Iniciando recorrido de "${PROJECT_ROOT}" en site ${siteId}`)

  const files = []
  const folders = []
  const queue = [{ folderPath: PROJECT_ROOT, relativePath: '' }]
  let scanned = 0

  while (queue.length > 0) {
    const { folderPath, relativePath } = queue.shift()

    let children
    try {
      children = await listChildren(siteId, folderPath)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(
        `[sharepoint-reader] No se pudo listar "${folderPath}":`,
        err?.statusCode,
        err?.message || err
      )
      continue
    }

    for (const item of children) {
      const childRel = relativePath
        ? `${relativePath}/${item.name}`
        : item.name
      const childFull = `${folderPath}/${item.name}`

      if (item.folder) {
        folders.push({
          id: item.id,
          name: item.name,
          relativePath: childRel,
          fullPath: childFull,
          parentPath: relativePath,
          childCount: item.folder.childCount ?? 0
        })
        queue.push({ folderPath: childFull, relativePath: childRel })
      } else if (item.file && isAllowedFile(item.name)) {
        files.push({
          id: item.id,
          name: item.name,
          relativePath: childRel,
          fullPath: childFull,
          parentPath: relativePath,
          size: item.size,
          lastModifiedDateTime: item.lastModifiedDateTime,
          webUrl: item.webUrl,
          downloadUrl: item['@microsoft.graph.downloadUrl'] || null,
          eTag: item.eTag
        })
        scanned += 1
      }
    }

    if (onProgress) onProgress(scanned, queue.length)
  }

  // eslint-disable-next-line no-console
  console.info(
    `[sharepoint-reader] Inventario completado: ${files.length} documentos en ${folders.length} carpetas`
  )

  return {
    siteId,
    files,
    folders,
    scannedAt: new Date().toISOString()
  }
}
